import { useState } from "react";
import { fleetSyncSubscriptionEnd } from "../../api/users";

type SyncResult = {
  dry_run: boolean;
  total_fleet_emails: number;
  updated_count: number;
  updated: { email: string; before: string | null; after: string; fleet_instance_ids: string[] }[];
  unchanged_count: number;
  fleet_only_count: number;
  fleet_only: { email: string; proposed_expiry: string }[];
};

/**
 * Fleet 邮箱+到期日 → kiro_users.subscription_end_at 同步页面。
 * 两步确认:先"预览"再"确认写入"。
 */
export function FleetSubscriptionSyncPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewResult, setPreviewResult] = useState<SyncResult | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [confirmResult, setConfirmResult] = useState<SyncResult | null>(null);

  const handlePreview = async () => {
    setLoading(true);
    setError(null);
    setPreviewResult(null);
    setConfirmed(false);
    setConfirmResult(null);
    try {
      const result = await fleetSyncSubscriptionEnd(false);
      setPreviewResult(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "预览失败");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fleetSyncSubscriptionEnd(true);
      setConfirmResult(result);
      setConfirmed(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "写入失败");
    } finally {
      setLoading(false);
    }
  };

  const displayResult = confirmResult ?? previewResult;

  return (
    <div>
      <h2 className="section-title">Fleet → 用户订阅到期日同步</h2>
      <div className="card" style={{ marginBottom: 12, fontSize: 13, color: "#475569" }}>
        <p>
          从 Kiro-Go 舰队实例的 per-key 邮箱关联记录中，提取每个 Kiro 账号邮箱对应的最晚实例到期日，
          将其同步写入 <code>kiro_users.subscription_end_at</code>。
        </p>
        <p>操作分两步：先"预览"查看将要更新的记录，确认无误后再"确认写入"。</p>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="card" style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <button className="primary" onClick={handlePreview} disabled={loading}>
          {loading && !confirmed ? "加载中..." : "预览同步结果"}
        </button>
        {previewResult && !confirmed && (
          <button
            className="primary"
            style={{ background: "#16a34a" }}
            onClick={handleConfirm}
            disabled={loading || previewResult.updated_count === 0}
          >
            {loading ? "写入中..." : `确认写入(${previewResult.updated_count} 条)`}
          </button>
        )}
        {confirmed && (
          <span style={{ color: "#16a34a", fontWeight: 500 }}>✓ 写入完成</span>
        )}
      </div>

      {displayResult && (
        <div className="card">
          <div className="section-title">
            {displayResult.dry_run ? "预览结果(未写入)" : "写入结果"}
          </div>
          <p style={{ fontSize: 13 }}>
            Fleet 中关联邮箱总数: <strong>{displayResult.total_fleet_emails}</strong> ·
            待更新: <strong>{displayResult.updated_count}</strong> ·
            无需更新: <strong>{displayResult.unchanged_count}</strong> ·
            仅在 Fleet 中存在(kiro_users 中找不到): <strong>{displayResult.fleet_only_count}</strong>
          </p>

          {displayResult.updated.length > 0 && (
            <>
              <div className="section-title" style={{ fontSize: 13 }}>待更新记录</div>
              <table>
                <thead>
                  <tr>
                    <th>邮箱</th>
                    <th>当前到期日</th>
                    <th>新到期日</th>
                    <th>来源实例</th>
                  </tr>
                </thead>
                <tbody>
                  {displayResult.updated.map((u) => (
                    <tr key={u.email}>
                      <td>{u.email}</td>
                      <td>{u.before ?? "-"}</td>
                      <td style={{ color: "#16a34a", fontWeight: 500 }}>{u.after}</td>
                      <td style={{ fontSize: 11, color: "#64748b" }}>
                        {u.fleet_instance_ids.slice(0, 3).join(", ")}
                        {u.fleet_instance_ids.length > 3 && "..."}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {displayResult.fleet_only.length > 0 && (
            <>
              <div className="section-title" style={{ fontSize: 13, marginTop: 12 }}>
                仅在 Fleet 中存在的邮箱(kiro_users 无对应记录,跳过)
              </div>
              <ul style={{ fontSize: 12, paddingLeft: 16 }}>
                {displayResult.fleet_only.slice(0, 20).map((r) => (
                  <li key={r.email}>
                    {r.email} → 建议到期日: {r.proposed_expiry.slice(0, 10)}
                  </li>
                ))}
                {displayResult.fleet_only.length > 20 && (
                  <li style={{ listStyle: "none", marginLeft: -16, color: "#94a3b8" }}>
                    ...共 {displayResult.fleet_only.length} 条,仅展示前 20 条
                  </li>
                )}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}
